import { isRecord } from '@tadpoles/common'

export const PushNotificationServiceWorkerMessageType = 'family-push-notification'
export const PushNotificationClientReadyMessageType = 'family-push-client-ready'

export type PushNotificationSource = 'native' | 'web'
export type PushNotificationInteraction = 'received' | 'clicked'
export type PushNotificationPresentation = 'displayed' | 'silent'

export type PushNotificationEvent = {
    interaction: PushNotificationInteraction
    payload: unknown
    presentation: PushNotificationPresentation
    source: PushNotificationSource
}

export type PushNotificationServiceWorkerMessage = {
    event: PushNotificationEvent
    type: typeof PushNotificationServiceWorkerMessageType
}

export type PushNotificationClientReadyMessage = {
    type: typeof PushNotificationClientReadyMessageType
}

export function createPushNotificationServiceWorkerMessage(
    event: PushNotificationEvent
): PushNotificationServiceWorkerMessage {
    return {
        type: PushNotificationServiceWorkerMessageType,
        event
    }
}

export function parsePushNotificationServiceWorkerMessage(
    value: unknown
): PushNotificationEvent | undefined {
    if (!isRecord(value) || value.type !== PushNotificationServiceWorkerMessageType) {
        return undefined
    }

    return parsePushNotificationEvent(value.event)
}

export function createPushNotificationClientReadyMessage(): PushNotificationClientReadyMessage {
    return {
        type: PushNotificationClientReadyMessageType
    }
}

export function isPushNotificationClientReadyMessage(value: unknown): boolean {
    return isRecord(value) && value.type === PushNotificationClientReadyMessageType
}

export function parseNativePushNotificationDetail(
    value: unknown
): Omit<PushNotificationEvent, 'source'> | undefined {
    if (!isRecord(value)) {
        return undefined
    }

    if (!isPushNotificationInteraction(value.interaction)) {
        return undefined
    }

    if (!isPushNotificationPresentation(value.presentation)) {
        return undefined
    }

    return {
        interaction: value.interaction,
        presentation: value.presentation,
        payload: value.payload
    }
}

function parsePushNotificationEvent(value: unknown): PushNotificationEvent | undefined {
    if (!isRecord(value)) {
        return undefined
    }

    if (!isPushNotificationSource(value.source)) {
        return undefined
    }

    const nativeDetail = parseNativePushNotificationDetail(value)
    if (!nativeDetail) {
        return undefined
    }

    return {
        ...nativeDetail,
        source: value.source
    }
}

function isPushNotificationSource(value: unknown): value is PushNotificationSource {
    return value === 'native' || value === 'web'
}

function isPushNotificationInteraction(value: unknown): value is PushNotificationInteraction {
    return value === 'received' || value === 'clicked'
}

function isPushNotificationPresentation(value: unknown): value is PushNotificationPresentation {
    return value === 'displayed' || value === 'silent'
}
